import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Loading from 'ui/atoms/Loading/Loading';

import { SelectCustomizable } from 'core/chatbot/components/atoms/legacyAtoms';
import Icon from 'core/modules/atoms/Icon';
import Spinner from 'ui/atoms/Spinner/Spinner';
import TopicRankingItem from './TopicRankingItem/TopicRankingItem';
import './_topic-ranking-section.scss';
import useTopicRankingSection from './useTopicRankingSection';

type TopicRankingSectionProps = {
  allCourseIds: string[];
  classValue: string;
  groupCourseMap: Record<string, string[]>;
  timePeriodValue: string;
};

function TopicRankingSection({ allCourseIds, classValue, groupCourseMap, timePeriodValue }: TopicRankingSectionProps): JSX.Element {
  const { t } = useTranslation(['chatbot']);
  const {
    currentCourseIds,
    currentEndDate,
    currentStartDate,
    disciplineFilter,
    disciplineOptions,
    hasMore,
    loading,
    loadingMore,
    loadMore,
    setDisciplineFilter,
    setSortBy,
    sortBy,
    totalFrequency,
    visibleTopics,
  } = useTopicRankingSection({
    allCourseIds,
    classValue,
    groupCourseMap,
    timePeriodValue,
  });

  const maxFrequency = visibleTopics.length > 0 ? visibleTopics[0].frequency : 1;

  const disciplineSelectOptions = useMemo(() => {
    const allOption = { label: t('chatbot:ai_analytics_topic_ranking_filter_all_disciplines'), value: 'all' };
    return [allOption, ...disciplineOptions];
  }, [disciplineOptions, t]);
  const selectedDiscipline = disciplineSelectOptions.find((o) => o.value === disciplineFilter) || disciplineSelectOptions[0];

  // const sortSelectOptions = useMemo(
  //   () => [
  //     { label: t('chatbot:ai_analytics_topic_ranking_sort_popularity'), value: 'popularity' },
  //     { label: t('chatbot:ai_analytics_topic_ranking_sort_students'), value: 'students' },
  //   ],
  //   [t]
  // );
  // const selectedSort = sortSelectOptions.find((o) => o.value === sortBy) || sortSelectOptions[0];

  return (
    <div className="topic-ranking-section">
      <div className="topic-ranking-section__header">
        <div className="topic-ranking-section__header-titles">
          <h2 className="topic-ranking-section__title">{t('chatbot:ai_analytics_topic_ranking_title')}</h2>
          <p className="topic-ranking-section__subtitle">{t('chatbot:ai_analytics_topic_ranking_subtitle', { count: totalFrequency })}</p>
        </div>
        {/* TODO: FASE 2: Añadir toggle para ver las topics en lista o en grid
        <div className="topic-ranking-section__header-actions">
          <div className="topic-ranking-section__view-toggle">
            <button className="topic-ranking-section__view-toggle-btn topic-ranking-section__view-toggle-btn--active" type="button" aria-label="List view">
              <Icon type="menu" size="default" />
            </button>
            <button className="topic-ranking-section__view-toggle-btn" disabled type="button" aria-label="Grid view">
              <Icon type="tools" size="default" />
            </button>
          </div>
        </div> */}
        <div className="topic-ranking-section__filters-row">
          <div className="topic-ranking-section__filters">
            <div className="topic-ranking-section__filter">
              <SelectCustomizable
                onChange={(option: { label: string; value: string } | null) => option && setDisciplineFilter(option.value)}
                options={disciplineSelectOptions}
                placeholder={t('chatbot:ai_analytics_topic_ranking_filter_discipline')}
                size="default"
                value={selectedDiscipline}
                variant="default"
              />
            </div>
            {/* TODO: FASE 2: Añadir filtro para ordenar las topics por popularidad o por usuarios únicos
            <div className="topic-ranking-section__filter">
              <label className="topic-ranking-section__filter-label">{t('chatbot:ai_analytics_topic_ranking_filter_sort_by')}</label>
              <SelectCustomizable
                onChange={(option: { label: string; value: string } | null) => option && setSortBy(option.value)}
                options={sortSelectOptions}
                placeholder={t('chatbot:ai_analytics_topic_ranking_filter_sort_by')}
                size="default"
                value={selectedSort}
                variant="default"
              />
            </div> */}
          </div>
        </div>
      </div>

      <div className="topic-ranking-section__filters-row">
        <p className="topic-ranking-section__description">{t('chatbot:ai_analytics_topic_ranking_description')}</p>
      </div>

      {loading ? (
        <div className="topic-ranking-section__loading">
          <Loading />
        </div>
      ) : visibleTopics.length > 0 ? (
        <>
          <div className="topic-ranking-section__list">
            {visibleTopics.map((topic, index) => (
              <TopicRankingItem
                colorIndex={index}
                courseIds={currentCourseIds}
                disciplineName={topic.disciplineName}
                endDate={currentEndDate}
                frequency={topic.frequency}
                key={`${topic.topicId || topic.topic}-${topic.disciplineId}`}
                maxFrequency={maxFrequency}
                startDate={currentStartDate}
                topic={topic.topic}
                topicId={topic.topicId}
              />
            ))}
          </div>
          {hasMore && (
            <button className="topic-ranking-section__load-more-btn" disabled={loadingMore} onClick={loadMore} type="button">
              {loadingMore ? (
                <Spinner />
              ) : (
                <>
                  {t('chatbot:ai_analytics_topic_ranking_load_more_simple')}
                  <Icon type="chevron-down" size="default" />
                </>
              )}
            </button>
          )}
        </>
      ) : (
        <p className="topic-ranking-section__empty">{t('chatbot:ai_analytics_topic_ranking_empty')}</p>
      )}
    </div>
  );
}

export default TopicRankingSection;
